import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Star, Filter, Search, ChevronDown, ArrowLeft, Info } from 'lucide-react';
import './Products.css';

const Products = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [prescriptionOnly, setPrescriptionOnly] = useState(false);

  const products = [
    {
      id: 1,
      name: 'Nova SN4001',
      category: 'daily',
      price: 29.99,
      originalPrice: 39.99,
      rating: 4.8,
      reviews: 124,
      waterContent: '42%',
      material: 'HEMA',
      image: '/api/placeholder/300/300',
      features: ['Sensitive Eyes', 'UV Protection', 'Breathable'],
      colors: ['Clear', 'Blue', 'Green'],
      prescription: true,
      inStock: true,
      bestseller: true
    },
    {
      id: 2,
      name: 'Nova SN4002',
      category: 'weekly',
      price: 24.99,
      originalPrice: null,
      rating: 4.7,
      reviews: 89,
      waterContent: '38%',
      material: 'HEMA',
      image: '/api/placeholder/300/300',
      features: ['Long Wear', 'Comfort Plus', 'Clear Vision'],
      colors: ['Clear', 'Hazel', 'Gray'],
      prescription: false,
      inStock: true,
      bestseller: false
    },
    {
      id: 3,
      name: 'Nova SN4003',
      category: 'monthly',
      price: 19.99,
      originalPrice: 24.99,
      rating: 4.9,
      reviews: 203,
      waterContent: '45%',
      material: 'Silicone Hydrogel',
      image: '/api/placeholder/300/300',
      features: ['Extended Wear', 'High Oxygen', 'Moisture Lock'],
      colors: ['Clear', 'Brown', 'Blue', 'Green'],
      prescription: true,
      inStock: true,
      bestseller: true
    },
    {
      id: 4,
      name: 'Nova SN4004',
      category: 'daily',
      price: 34.99,
      originalPrice: null,
      rating: 4.6,
      reviews: 67,
      waterContent: '48%',
      material: 'HEMA',
      image: '/api/placeholder/300/300',
      features: ['Premium Comfort', 'All Day Wear', 'Easy Handling'],
      colors: ['Clear', 'Violet', 'Honey'],
      prescription: true,
      inStock: false,
      bestseller: false
    },
    {
      id: 5,
      name: 'Nova SN4005',
      category: 'weekly',
      price: 27.99,
      originalPrice: null,
      rating: 4.5,
      reviews: 156,
      waterContent: '40%',
      material: 'Silicone Hydrogel',
      image: '/api/placeholder/300/300',
      features: ['Astigmatism', 'Sharp Vision', 'Stable Fit'],
      colors: ['Clear'],
      prescription: true,
      inStock: true,
      bestseller: false
    },
    {
      id: 6,
      name: 'Nova SN4006',
      category: 'monthly',
      price: 22.99,
      originalPrice: 29.99,
      rating: 4.8,
      reviews: 198,
      waterContent: '42%',
      material: 'HEMA',
      image: '/api/placeholder/300/300',
      features: ['Multifocal', 'Reading Support', 'Distance Vision'],
      colors: ['Clear', 'Blue', 'Brown'],
      prescription: true,
      inStock: true,
      bestseller: true
    }
  ];

  const categories = [
    { id: 'all', name: 'All Products', count: products.length },
    { id: 'daily', name: 'Daily', count: products.filter(p => p.category === 'daily').length },
    { id: 'weekly', name: 'Weekly', count: products.filter(p => p.category === 'weekly').length },
    { id: 'monthly', name: 'Monthly', count: products.filter(p => p.category === 'monthly').length }
  ];

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesColors = selectedColors.length === 0 || 
                         selectedColors.some(color => product.colors.includes(color));
    const matchesPrescription = !prescriptionOnly || product.prescription;
    
    return matchesCategory && matchesSearch && matchesColors && matchesPrescription;
  });

  // Get all unique colors from products
  const allColors = [...new Set(products.flatMap(p => p.colors))];

  // Filter handler functions
  const handleColorChange = (color) => {
    setSelectedColors(prev => 
      prev.includes(color) 
        ? prev.filter(c => c !== color)
        : [...prev, color]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategory('all');
    setSelectedColors([]);
    setPrescriptionOnly(false);
    setSearchTerm('');
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'reviews':
        return b.reviews - a.reviews;
      case 'category':
        return a.category.localeCompare(b.category);
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const ProductCard = ({ product }) => (
    <div className="gallery-card">
      {product.bestseller && <div className="bestseller-badge">Featured</div>}
      {product.originalPrice && (
        <div className="new-badge">New Model</div>
      )}
      
      <div className="product-image">
        <div className="placeholder-image">
          <Eye size={64} />
        </div>
        <div className="image-overlay">
          <button className="view-details-btn">
            <Info size={18} />
            View Details
          </button>
        </div>
      </div>
      
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        
        <div className="product-rating">
          <div className="stars">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                size={16} 
                className={i < Math.floor(product.rating) ? 'star-filled' : 'star-empty'} 
              />
            ))}
          </div>
          <span className="rating-text">({product.reviews} reviews)</span>
        </div>
        
        <div className="product-features">
          {product.features.slice(0, 3).map((feature, index) => (
            <span key={index} className="feature-tag">{feature}</span>
          ))}
          {product.prescription && (
            <span className="feature-tag prescription">Prescription Available</span>
          )}
        </div>
        
        <div className="product-colors">
          <span className="colors-label">Available Colors:</span>
          <div className="color-options">
            {product.colors.map((color, index) => (
              <span key={index} className={`color-tag color-${color.toLowerCase()}`}>
                {color}
              </span>
            ))}
          </div>
        </div>
        
        <div className="product-specs">
          <div className="spec-item">
            <span className="spec-label">Water Content:</span>
            <span className="spec-value">{product.waterContent}</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">Material:</span>
            <span className="spec-value">{product.material}</span>
          </div>
          <div className="spec-item">
            <span className="spec-label">Category:</span>
            <span className="spec-value">{product.category.charAt(0).toUpperCase() + product.category.slice(1)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <div className="container">
          <Link to="/" className="back-to-home">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1>Nova Lenses Gallery</h1>
          <p>Explore our premium contact lens collection</p>
        </div>
      </div>

      <div className="container">
        <div className="products-layout">
          {/* Sidebar */}
          <aside className="products-sidebar">
            <div className="sidebar-header">
              <h2>Filter Gallery</h2>
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear All
              </button>
            </div>
            
            <div className="sidebar-section">
              <h3>Categories</h3>
              <div className="category-list">
                {categories.map(category => (
                  <button
                    key={category.id}
                    className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name} ({category.count})
                  </button>
                ))}
              </div>
            </div>

            <div className="sidebar-section">
              <h3>Colors</h3>
              <div className="color-filters">
                {allColors.map(color => (
                  <label key={color} className="filter-checkbox color-filter">
                    <input 
                      type="checkbox" 
                      checked={selectedColors.includes(color)}
                      onChange={() => handleColorChange(color)}
                    />
                    <span className={`color-indicator color-${color.toLowerCase()}`}></span>
                    <span>{color}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="sidebar-section">
              <h3>Prescription Available</h3>
              <div className="prescription-filter">
                <label className="filter-checkbox">
                  <input 
                    type="checkbox" 
                    checked={prescriptionOnly}
                    onChange={(e) => setPrescriptionOnly(e.target.checked)}
                  />
                  <span>Show Prescription Models Only</span>
                </label>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="products-main">
            {/* Filters Bar */}
            <div className="filters-bar">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="sort-controls">
                <button 
                  className="mobile-filter-btn"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={18} />
                  Filters
                </button>
                
                <div className="sort-dropdown">
                  <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="name">Sort by Name</option>
                    <option value="rating">Highest Rated</option>
                    <option value="reviews">Most Reviewed</option>
                    <option value="category">By Category</option>
                  </select>
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>

            {/* Results Info */}
            <div className="results-info">
              <p>Viewing {sortedProducts.length} lens models</p>
              {(selectedColors.length > 0 || prescriptionOnly) && (
                <div className="active-filters">
                  <span className="filters-label">Active filters:</span>
                  {selectedColors.map(color => (
                    <span key={color} className="active-filter-tag">
                      {color}
                      <button onClick={() => handleColorChange(color)}>×</button>
                    </span>
                  ))}
                  {prescriptionOnly && (
                    <span className="active-filter-tag">
                      Prescription
                      <button onClick={() => setPrescriptionOnly(false)}>×</button>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Gallery Grid */}
            <div className="gallery-grid">
              {sortedProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            {sortedProducts.length === 0 && (
              <div className="no-results">
                <Eye size={64} />
                <h3>No lens models found</h3>
                <p>Try adjusting your filters or search terms</p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile Filters Overlay */}
      {showFilters && (
        <div className="mobile-filters-overlay">
          <div className="mobile-filters-content">
            <div className="mobile-filters-header">
              <h3>Filters</h3>
              <button onClick={() => setShowFilters(false)}>×</button>
            </div>
            {/* Mobile filter content would go here */}
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
